import { useEffect, useRef, useState } from "react";
import { NavLink, useRevalidator } from "react-router-dom";
import { ExternalLink, GitBranch, Loader, Pencil, PhoneForwarded, Rocket, Search } from "lucide-react";
import { Panel, Section, StatusBadge } from "@/components/layout-2/ds";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SimpleTooltip } from "@/components/ui/simple-tooltip";
import { cn } from "@/lib/utils";
import { DataService } from "@/services/data";
import { VersionStatus } from "@/types/dialer/graph-view";
import type { BadgeValue } from "@/types/dynamic-detail";
import { DeployTargetOption } from "@/types/dialer/workflow";
import { workflowEditorPath } from "@/utils/dialer-breadcrumbs";
import { formatParis } from "@/utils/dialer-datetime";
import { useDebounce } from "@/utils/misc";
import { DeployDialog } from "./deploy-dialog";

export type VersionPanelRow = {
    id: number;
    version: number;
    status: VersionStatus;
    status_badge?: BadgeValue;
    notes?: string | null;
    created_by?: { id: number; name: string } | null;
    created_at: string;
    published_at?: string | null;
    deployed_in: { campaign_code: string; campaign_name: string; slot: string }[];
    issue_counts: { error: number; warning: number };
    deployable: boolean;
};

export type WorkflowVersionsPanelContent = {
    workflow_id: number;
    workflow_code: string;
    workflow_name: string;
    search_url: string;
    recent: VersionPanelRow[];
    in_use: VersionPanelRow[];
    campaigns: {
        id: number;
        code: string;
        name: string;
        slots: string[];
    }[];
    deploy_targets: DeployTargetOption[];
    permissions: { edit: boolean; deploy: boolean; simulate: boolean };
    has_open_draft: boolean;
};

type VersionSearchResponse = {
    query: string;
    results: VersionPanelRow[];
    has_more: boolean;
};

function versionPath(content: WorkflowVersionsPanelContent, row: VersionPanelRow) {
    return `/outils/campagnes/workflows/${content.workflow_code}/v/${row.version}`;
}

function VersionRow({ content, row, onDeploy }: { content: WorkflowVersionsPanelContent; row: VersionPanelRow; onDeploy: (row: VersionPanelRow) => void }) {
    const canEdit = content.permissions?.edit && row.status !== "deprecated";
    const deployBlocked = !row.deployable || row.issue_counts.error > 0 || content.deploy_targets.length === 0;
    const deployReason = !row.deployable
        ? "Deprecated versions cannot be deployed."
        : row.issue_counts.error > 0
          ? `${row.issue_counts.error} blocking problem${row.issue_counts.error > 1 ? "s" : ""} must be resolved before deployment.`
          : "No campaign slot is available.";
    return (
        <li className="border-b py-2 last:border-b-0">
            <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-mono text-sm font-semibold">v{row.version}</span>
                        <StatusBadge value={row.status_badge} />
                        {row.deployed_in.length > 0 && <Badge tone="info">Currently used</Badge>}
                    </div>
                    <p className="mt-0.5 truncate text-xs text-gray-500">
                        {formatParis(row.created_at)}
                        {row.created_by?.name ? ` · ${row.created_by.name}` : ""}
                    </p>
                    {row.deployed_in.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                            {row.deployed_in.map(target => (
                                <Badge key={`${target.campaign_code}-${target.slot}`} tone="neutral">
                                    {target.slot} @ {target.campaign_code}
                                </Badge>
                            ))}
                        </div>
                    )}
                </div>
                <div className="flex shrink-0 items-center">
                    {canEdit && (
                        <SimpleTooltip
                            message={content.has_open_draft ? "Resume canvas draft. The existing draft keeps its current base version." : `Edit from version ${row.version} on the canvas.`}
                            asChild
                        >
                            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                                <NavLink
                                    to={workflowEditorPath(content.workflow_id, { from_version: row.version })}
                                    aria-label={content.has_open_draft ? "Resume canvas draft" : `Edit from version ${row.version}`}
                                >
                                    <Pencil className="h-4 w-4" />
                                </NavLink>
                            </Button>
                        </SimpleTooltip>
                    )}
                    {content.permissions?.deploy && row.deployed_in.length === 0 && (
                        <SimpleTooltip message={deployBlocked ? deployReason : `Deploy version ${row.version}`} asChild>
                            <span tabIndex={deployBlocked ? 0 : undefined} className="inline-flex">
                                <Button variant="ghost" size="icon" className="h-8 w-8" disabled={deployBlocked} onClick={() => onDeploy(row)} aria-label={`Deploy version ${row.version}`}>
                                    <Rocket className="h-4 w-4" />
                                </Button>
                            </span>
                        </SimpleTooltip>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                        <NavLink to={versionPath(content, row)} aria-label={`Open version ${row.version}`}>
                            <ExternalLink className="h-4 w-4" />
                        </NavLink>
                    </Button>
                </div>
            </div>
        </li>
    );
}

const INITIAL_CAMPAIGN_LIMIT = 4;

function slotLabel(slot: string) {
    return slot.charAt(0).toUpperCase() + slot.slice(1);
}

function CampaignsContent({ campaigns }: { campaigns: WorkflowVersionsPanelContent["campaigns"] }) {
    const [showAll, setShowAll] = useState(false);
    const displayedCampaigns = showAll ? campaigns : campaigns.slice(0, INITIAL_CAMPAIGN_LIMIT);

    if (campaigns.length === 0) {
        return <p className="py-2 text-sm text-gray-500">Not used by any campaign.</p>;
    }

    return (
        <>
            <ul className={cn(showAll && "max-h-48 overflow-y-auto pr-1")}>
                {displayedCampaigns.map(campaign => (
                    <li key={campaign.id} className="border-b last:border-b-0">
                        <NavLink
                            to={`/dialer-campaigns/${campaign.id}/informations`}
                            aria-label={`Open campaign ${campaign.name}`}
                            className="flex items-center gap-2 px-1 py-1.5 transition-colors hover:bg-gray-50"
                        >
                            <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm text-blue-700 hover:text-blue-900">{campaign.name}</span>
                                <span className="block truncate font-mono text-xs text-gray-500">{campaign.code}</span>
                            </span>
                            <span className="flex shrink-0 flex-wrap justify-end gap-1">
                                {campaign.slots.map(slot => (
                                    <Badge key={slot} tone="neutral">
                                        {slotLabel(slot)}
                                    </Badge>
                                ))}
                            </span>
                        </NavLink>
                    </li>
                ))}
            </ul>
            {campaigns.length > INITIAL_CAMPAIGN_LIMIT && (
                <Button type="button" variant="ghost" size="sm" className="mt-1 h-7 px-2" aria-expanded={showAll} onClick={() => setShowAll(value => !value)}>
                    {showAll ? "Show less" : `Show all (${campaigns.length})`}
                </Button>
            )}
        </>
    );
}

export function WorkflowVersionsPanel({ content }: { content: WorkflowVersionsPanelContent }) {
    const revalidator = useRevalidator();
    const [query, setQuery] = useState("");
    const debouncedQuery = useDebounce(query.trim(), 300);
    const [results, setResults] = useState<VersionPanelRow[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [deploying, setDeploying] = useState<VersionPanelRow | null>(null);
    const requestSequence = useRef(0);
    // Recent rows already carry their deployment badges. Only repeat an in-use version
    // as a separate row when it is old enough to fall outside that bounded list.
    const recentIds = new Set(content.recent.map(row => row.id));
    const olderInUse = content.in_use.filter(row => !recentIds.has(row.id));

    useEffect(() => {
        const sequence = ++requestSequence.current;
        if (!debouncedQuery) {
            setResults([]);
            setHasMore(false);
            setLoading(false);
            setError(false);
            return;
        }

        const search = async () => {
            setLoading(true);
            setError(false);
            try {
                const response = (await DataService.client.url(content.search_url).query({ q: debouncedQuery }).get().json()) as VersionSearchResponse;
                if (sequence !== requestSequence.current) return;
                setResults(response.results ?? []);
                setHasMore(response.has_more ?? false);
            } catch {
                if (sequence === requestSequence.current) setError(true);
            } finally {
                if (sequence === requestSequence.current) setLoading(false);
            }
        };
        void search();
    }, [content.search_url, debouncedQuery]);

    return (
        <div className="mt-2 space-y-2">
            {/* A side panel of the record: a white Panel each, titled like a related list, no nested card. */}
            <Panel>
                <div role="region" aria-labelledby="workflow-used-by-campaigns">
                    <h3 id="workflow-used-by-campaigns" className="mb-2 flex min-w-0 items-center gap-2 text-base font-bold">
                        <PhoneForwarded className="h-4 w-4 text-gray-500" />
                        Used by campaigns ({(content.campaigns ?? []).length})
                    </h3>
                    <CampaignsContent campaigns={content.campaigns ?? []} />
                </div>
            </Panel>

            <Panel>
                <div role="region" aria-labelledby="workflow-versions">
                    <h3 id="workflow-versions" className="mb-3 flex min-w-0 items-center gap-2 text-base font-bold">
                        <GitBranch className="h-4 w-4 text-gray-500" />
                        Workflow versions
                    </h3>
                    <div>
                        {olderInUse.length > 0 && (
                            <Section title="In use">
                                <ul>
                                    {olderInUse.map(row => (
                                        <VersionRow key={row.id} content={content} row={row} onDeploy={setDeploying} />
                                    ))}
                                </ul>
                            </Section>
                        )}

                        <Section title="Recent versions">
                            {content.recent.length > 0 ? (
                                <ul className="max-h-72 overflow-y-auto pr-1">
                                    {content.recent.map(row => (
                                        <VersionRow key={row.id} content={content} row={row} onDeploy={setDeploying} />
                                    ))}
                                </ul>
                            ) : (
                                <p className="py-2 text-sm text-gray-500">No version yet. Use Edit on canvas to create the first immutable version.</p>
                            )}
                        </Section>

                        {content.recent.length > 0 && (
                            <Section title={<label htmlFor="workflow-version-search">Find an older version</label>}>
                                <Input
                                    id="workflow-version-search"
                                    value={query}
                                    onChange={event => setQuery(event.target.value)}
                                    placeholder="Version, author, notes or status"
                                    icon={Search}
                                    autoComplete="off"
                                />
                                <div className="min-h-5" aria-live="polite">
                                    {loading ? (
                                        <p className="flex items-center gap-1.5 text-sm text-gray-500">
                                            <Loader className="h-4 w-4 animate-spin" /> Searching…
                                        </p>
                                    ) : error ? (
                                        <p className="text-sm text-gray-500">Search failed. Check your connection and try again.</p>
                                    ) : debouncedQuery && results.length === 0 ? (
                                        <p className="text-sm text-gray-500">No matching version.</p>
                                    ) : results.length > 0 ? (
                                        <ul className="max-h-64 overflow-y-auto pr-1">
                                            {results.map(row => (
                                                <VersionRow key={row.id} content={content} row={row} onDeploy={setDeploying} />
                                            ))}
                                        </ul>
                                    ) : null}
                                    {hasMore && <p className="mt-1 text-xs text-gray-500">More matches exist. Refine your search.</p>}
                                </div>
                            </Section>
                        )}
                    </div>
                </div>
            </Panel>
            {deploying && (
                <DeployDialog
                    open
                    onOpenChange={open => !open && setDeploying(null)}
                    workflowCode={content.workflow_code}
                    workflowName={content.workflow_name}
                    versionId={deploying.id}
                    versionNumber={deploying.version}
                    targets={content.deploy_targets}
                    onDone={() => revalidator.revalidate()}
                />
            )}
        </div>
    );
}
